import type { ComponentType } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import {
  ListBullets,
  ListNumbers,
  TextHOne,
  TextHTwo,
  TextHThree,
  TextHFour,
  TextHFive,
  TextHSix,
  Highlighter,
} from '@phosphor-icons/react';
import type { Ctx } from '@milkdown/kit/ctx';
import { commandsCtx, editorViewCtx } from '@milkdown/kit/core';
import type { MarkType } from '@milkdown/kit/prose/model';
import {
  headingSchema,
  paragraphSchema,
  bulletListSchema,
  orderedListSchema,
  setBlockTypeCommand,
  wrapInBlockTypeCommand,
  liftListItemCommand,
  isMarkSelectedCommand,
} from '@milkdown/kit/preset/commonmark';

import { highlightSchema, toggleHighlightCommand } from '../plugins/textHighlight';
import { isMilkdownContextNotFoundError } from '../collaboration/constants';
import { getNodeAtCursor } from '../utils/getNodeAtCursor';

const HEADING_LEVELS = [1, 2, 3, 4, 5, 6] as const;

function getEditorView(ctx: Ctx) {
  try {
    return ctx.get(editorViewCtx);
  } catch (error) {
    if (isMilkdownContextNotFoundError(error)) return null;
    throw error;
  }
}

function getHeadingLevelAtSelection(ctx: Ctx): number | null {
  const view = getEditorView(ctx);
  if (!view) return null;
  const result = getNodeAtCursor(view, 'heading');

  return result ? (result.node.attrs.level as number) : null;
}

function isInListOfType(ctx: Ctx, listTypeName: 'bullet_list' | 'ordered_list'): boolean {
  const view = getEditorView(ctx);
  if (!view) return false;

  return getNodeAtCursor(view, listTypeName) != null;
}

function createHeadingToolbarItem(level: (typeof HEADING_LEVELS)[number]) {
  let icon: React.ReactNode;
  switch (level) {
    case 1:
      icon = <TextHOne size={24} aria-label={`Heading ${level}`} />;
      break;
    case 2:
      icon = <TextHTwo size={24} aria-label={`Heading ${level}`} />;
      break;
    case 3:
      icon = <TextHThree size={24} aria-label={`Heading ${level}`} />;
      break;
    case 4:
      icon = <TextHFour size={24} aria-label={`Heading ${level}`} />;
      break;
    case 5:
      icon = <TextHFive size={24} aria-label={`Heading ${level}`} />;
      break;
    case 6:
      icon = <TextHSix size={24} aria-label={`Heading ${level}`} />;
      break;
    default:
      icon = <TextHOne size={24} aria-label={`Heading ${level}`} />;
      break;
  }

  return {
    icon: renderToStaticMarkup(icon),
    active: (ctx: Ctx) => getHeadingLevelAtSelection(ctx) === level,
    onRun: (ctx: Ctx) => {
      const commands = ctx.get(commandsCtx);
      const currentLevel = getHeadingLevelAtSelection(ctx);
      if (currentLevel === level) {
        commands.call(setBlockTypeCommand.key, {
          nodeType: paragraphSchema.type(ctx),
        });
      } else {
        commands.call(setBlockTypeCommand.key, {
          nodeType: headingSchema.type(ctx),
          attrs: { level },
        });
      }
    },
  };
}

function createListToolbarItem(
  Icon: ComponentType<{ size?: number | string; 'aria-label'?: string }>,
  label: string,
  listTypeName: 'bullet_list' | 'ordered_list',
  getNodeType: (ctx: Ctx) => ReturnType<typeof bulletListSchema.type>,
) {
  return {
    icon: renderToStaticMarkup(<Icon size={24} aria-label={label} />),
    active: (ctx: Ctx) => isInListOfType(ctx, listTypeName),
    onRun: (ctx: Ctx) => {
      const commands = ctx.get(commandsCtx);
      if (isInListOfType(ctx, listTypeName)) {
        commands.call(liftListItemCommand.key);
      } else {
        commands.call(wrapInBlockTypeCommand.key, { nodeType: getNodeType(ctx) });
      }
    },
  };
}

function createHighlightToolbarItem(
  Icon: ComponentType<{ size?: number | string; 'aria-label'?: string }>,
  label: string,
  _key: string,
  getMarkType: (ctx: Ctx) => MarkType,
): FormattingToolbarItem {
  return {
    icon: renderToStaticMarkup(<Icon size={24} aria-label={label} />),
    active: (ctx: Ctx) => {
      const commands = ctx.get(commandsCtx);

      return commands.call(isMarkSelectedCommand.key, getMarkType(ctx));
    },
    onRun: (ctx: Ctx) => {
      const commands = ctx.get(commandsCtx);
      commands.call(toggleHighlightCommand.key);
    },
  };
}

/** Toolbar item shape expected by Crepe formatting group (icon, active, onRun). */
export type FormattingToolbarItem = {
  icon: string;
  active: (ctx: Ctx) => boolean;
  onRun: (ctx: Ctx) => void;
};

/**
 * Add heading (H1–H6) and list (bullet, ordered) toolbar items to the formatting group.
 * Call from Crepe Toolbar buildToolbar with the result of builder.getGroup('formatting').
 *
 * **Toolbar CSS coupling:** `theme/overrides.css` hides buttons 5–12 when the selection is in a
 * table cell (`data-bf-formatting-toolbar-table-cell` on `body`). That assumes Crepe prepends
 * bold, italic, strikethrough (3), then this function adds highlight, H1–H6, bullet, ordered (9).
 * Change order here only if you update the nth-child range in overrides.css.
 */
export function addFormattingToolbarItems(formattingGroup: {
  addItem: (key: string, item: FormattingToolbarItem) => unknown;
}) {
  // Add Highlight toolbar item
  formattingGroup.addItem(
    'highlight',
    createHighlightToolbarItem(Highlighter, 'Highlight', 'highlight', (ctx) => highlightSchema.type(ctx)),
  );
  HEADING_LEVELS.forEach((level) => {
    formattingGroup.addItem(`h${level}`, createHeadingToolbarItem(level));
  });
  formattingGroup.addItem(
    'bullet-list',
    createListToolbarItem(ListBullets, 'Bullet list', 'bullet_list', (ctx) => bulletListSchema.type(ctx)),
  );
  formattingGroup.addItem(
    'ordered-list',
    createListToolbarItem(ListNumbers, 'Ordered list', 'ordered_list', (ctx) => orderedListSchema.type(ctx)),
  );
}
